import { Button } from "components/ui/Button";
import classNames from "components/ui/classNames";
import { genStandardizedCountries } from "components/stocks/modern/OperatingSegments/genStandardizedCountries";
import uniqBy from "lodash/uniqBy";
import { FormattedMessage, useIntl } from "react-intl";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/Popover";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInputOriginal,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "components/ui/Command";
import { useState } from "react";

interface CountryComboboxProps {
  country: string;
  withoutLabel?: boolean;
  onCountryChange: (country: string) => void;
}

export default function CountryCombobox({ country, withoutLabel, onCountryChange }: CountryComboboxProps) {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const standardizedCountries = uniqBy(genStandardizedCountries(intl), "isoCountryCode").sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  const priorityCountryCodes = ["BR", "US", "MX", "PR", "PO", "CA", "GB"];
  const priorityCountries = standardizedCountries
    .filter(({ isoCountryCode }) => priorityCountryCodes.includes(isoCountryCode))
    .sort((a, b) => priorityCountryCodes.indexOf(a.isoCountryCode) - priorityCountryCodes.indexOf(b.isoCountryCode));
  const allOtherCountries = standardizedCountries.filter(({ isoCountryCode }) => {
    return !priorityCountryCodes.includes(isoCountryCode);
  });
  const countriesList = [...priorityCountries, ...allOtherCountries];

  const selectedCountry = countriesList.find((node) => node.isoCountryCode === country);
  const placeholder = intl.formatMessage({
    defaultMessage: "Selecione o país de Origem",
    id: "2FbFrw",
  });

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild={true}>
        <label htmlFor="country" className="pointer grid gap-y-3">
          {!withoutLabel && (
            <strong className="text-sm tracking-wide text-neutral-900">
              <FormattedMessage defaultMessage="País" description="Página de Configurações da conta" id="lRho2v" />
            </strong>
          )}

          <Button
            variant="outline"
            role="combobox"
            type="button"
            className={classNames(
              "flex h-10 w-full justify-between rounded-md border border-slate-300 px-3 py-2 text-sm",
              "file:border-0 file:bg-transparent file:text-sm file:font-medium",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300 dark:focus-visible:outline-gray-600",
              {
                "text-slate-400": !country,
              },
            )}
          >
            {country && selectedCountry ? selectedCountry.name : placeholder}
            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </label>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command
          value={country}
          disablePointerSelection={true}
          onValueChange={(value) => {
            if (country !== "") {
              setIsOpen(false);
            }
            onCountryChange(value);
          }}
        >
          <CommandInputOriginal
            placeholder={intl.formatMessage({ defaultMessage: "Procure por um país...", id: "sFjO8l" })}
          />
          <CommandList>
            <CommandEmpty>{intl.formatMessage({ defaultMessage: "País não encontrado", id: "CDMRXp" })}</CommandEmpty>
            <CommandGroup>
              {priorityCountries.map((country) => (
                <CommandItem
                  key={country.isoCountryCode}
                  value={country.isoCountryCode}
                  keywords={[country.name, country.isoCountryCode]}
                  className="flex items-center"
                >
                  <CheckIcon
                    className={classNames(
                      "mr-2 h-4 w-4",
                      selectedCountry && country.isoCountryCode === selectedCountry.isoCountryCode
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />

                  <img
                    src={`https://flagcdn.com/16x12/${country.isoCountryCode.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/32x24/${country.isoCountryCode.toLowerCase()}.png 2x, https://flagcdn.com/48x36/${country.isoCountryCode.toLowerCase()}.png 3x`}
                    width="16"
                    height="12"
                    alt={country.name}
                  />
                  {country.name}
                </CommandItem>
              ))}
              <CommandSeparator className="dark:bg-slate-600/40" />
              {allOtherCountries.map((country) => (
                <CommandItem
                  key={country.isoCountryCode}
                  value={country.isoCountryCode}
                  keywords={[country.name, country.isoCountryCode]}
                  className="flex items-center"
                >
                  <CheckIcon
                    className={classNames(
                      "mr-2 h-4 w-4",
                      selectedCountry && country.isoCountryCode === selectedCountry.isoCountryCode
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  <img
                    src={`https://flagcdn.com/16x12/${country.isoCountryCode.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/32x24/${country.isoCountryCode.toLowerCase()}.png 2x, https://flagcdn.com/48x36/${country.isoCountryCode.toLowerCase()}.png 3x`}
                    width="16"
                    height="12"
                    alt={country.name}
                  />
                  {country.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
